/******************************************************************************
 * AutoDiagTable: the list of Auto Diag compliance records ("auto-diagnostics")
 * submitted by technicians. Used by the super-admin Auto Diag screen (all
 * technicians, filterable) and by a technician's own page (fixed technician).
 *
 * Endpoint (core):
 *   GET admin/auto-diag/submissions?page&size&sort&technicianId
 *                                              -> paginated Spring Page of records
 *
 * Compliance data (plate / mileage / driving licence / photo) is sensitive and
 * must never be logged.
 *****************************************************************************/

/* Imports */
import { useCallback, useEffect, useState } from "react";
import { Typography } from "@material-tailwind/react";
import { ShieldCheckIcon, ShieldExclamationIcon } from "@heroicons/react/24/outline";
import AutoDiagPhotoLink from "./AutoDiagPhotoLink";
import { t } from "i18next";
/***/

/* Components */
import Loading from "../../loading/Loading";
import { Table } from "../../table/Table";
import { Pagination } from "../../pagination/Pagination";
/***/

/* Services */
import { api } from "../../../services/api/api";
import { showApiErrorToast } from "../../../services/api/error";
/***/

/* Types */
import { AutoDiagSubmission, SpringPage } from "../../../types/auto-diag/autoDiagSubmission";
import { useTablePageSize } from "../../../hooks/useTablePageSize";
/***/

/**
 * formatDate: locale-aware date/time rendering, or a dash when absent.
 * @param value - ISO date string or null
 */
const formatDate = (value: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  return isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

/**
* AutoDiagTable: paginated Auto Diag records, optionally scoped to one technician.
*
* @param technicianId - technician to scope to; "" or undefined lists every technician
* @param technicianNames - id -> display name, for the technician column
* @param showTechnicianColumn - hidden on a technician's own page, where it would repeat the title
*/
export default function AutoDiagTable({
  technicianId = "",
  technicianNames,
  showTechnicianColumn = true
}: {
  technicianId?: string,
  technicianNames?: Map<string, string>,
  showTechnicianColumn?: boolean
}) {
  const [submissions, setSubmissions] = useState<AutoDiagSubmission[] | null>(null);
  const [pageState, setPageState] = useState<number>(0);
  const [pageNumberState, setPageNumberState] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // A change of technician invalidates the page cursor: page 3 of one technician's records is
  // meaningless for another's, and would show an empty table with no explanation.
  useEffect(() => {
    setPageState(0);
  }, [technicianId]);

  const pageSize = useTablePageSize();

  /* Un changement de taille invalide le curseur de page : l'élément 181 est en page 9 à 20 par page
   * et en page 2 à 100. Repartir de la première page est le seul comportement qui ne montre jamais
   * une page vide. */
  useEffect(() => { setPageState(0); }, [pageSize]);

  /**
  * loadSubmissions: fetch a page of Auto Diag submissions, newest first,
  * optionally scoped to the selected technician.
  */
  const loadSubmissions = useCallback(() => {
    setIsLoading(true);
    let url = `admin/auto-diag/submissions?page=${pageState}&size=${pageSize}&sort=created,desc`;
    if (technicianId) url += `&technicianId=${encodeURIComponent(technicianId)}`;

    api.get(url)
      .then((ret) => {
        const page = ret as SpringPage<AutoDiagSubmission>;
        setSubmissions(page.content || []);
        setPageNumberState(page.totalPages || 1);
      })
      .catch((err) => {
        console.error(err);
        showApiErrorToast(err, { fallbackMessage: t("admin:autoDiag.loadError") });
        setSubmissions([]);
        setPageNumberState(1);
      })
      .finally(() => setIsLoading(false));
  }, [pageState, technicianId, pageSize]);
  /***/

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const TABLE_HEAD = [
    ...(showTechnicianColumn ? [{ label: t("common:technician"), orderKey: null }] : []),
    { label: t("admin:autoDiag.columns.source"), orderKey: null },
    { label: t("admin:autoDiag.columns.plate"), orderKey: null },
    { label: t("admin:autoDiag.columns.mileage"), orderKey: null },
    { label: t("admin:autoDiag.columns.weight"), orderKey: null },
    { label: t("admin:autoDiag.columns.licence"), orderKey: null },
    { label: t("admin:autoDiag.columns.capturedAt"), orderKey: null },
    { label: t("admin:autoDiag.columns.submittedAt"), orderKey: null },
    { label: t("admin:autoDiag.columns.photo"), orderKey: null }
  ];

  if (submissions === null || isLoading) return <Loading />;

  return (
    <>
      <Table tableHead={TABLE_HEAD}
        emptyTable={submissions.length === 0 ? t("admin:autoDiag.empty") : false}>
        {submissions.map((submission) => (
          <tr key={submission.id} className="border-b last:border-b-0">
            {showTechnicianColumn &&
              <td className="p-3">
                <Typography variant="small">
                  {technicianNames?.get(submission.technicianId) || submission.technicianId}
                </Typography>
              </td>}
            <td className="p-3">
              <Typography variant="small">
                {t("admin:autoDiag.sources." + submission.source)}
              </Typography>
            </td>
            <td className="p-3">
              <Typography variant="small">{submission.plate || "—"}</Typography>
            </td>
            <td className="p-3">
              <Typography variant="small">
                {submission.mileage != null ? `${submission.mileage} km` : "—"}
              </Typography>
            </td>
            <td className="p-3">
              <Typography variant="small">
                {submission.weight != null ? submission.weight : "—"}
              </Typography>
            </td>
            <td className="p-3">
              {submission.drivingLicenceValid ?
                <ShieldCheckIcon className="w-5 text-green-500" title={t("admin:autoDiag.licenceValid")} />
                :
                <ShieldExclamationIcon className="w-5 text-red-500" title={t("admin:autoDiag.licenceInvalid")} />}
            </td>
            <td className="p-3">
              <Typography variant="small">{formatDate(submission.capturedAt)}</Typography>
            </td>
            <td className="p-3">
              <Typography variant="small">{formatDate(submission.submittedAt)}</Typography>
            </td>
            <td className="p-3">
              {submission.hasPhoto ?
                <AutoDiagPhotoLink submissionId={submission.id} />
                :
                <Typography variant="small" className="text-gray-400">—</Typography>}
            </td>
          </tr>
        ))}
      </Table>

      {pageNumberState > 1 &&
        <Pagination actualPage={pageState}
          pageNumber={pageNumberState}
          changePage={setPageState} />}
    </>
  );
}
/***/
